import { usePecField } from 'components/form/final-form/hooks/useField'
import { useProcedimentosAutomaticosQuery } from 'graphql/hooks.generated'
import { useCallback } from 'react'
import { emptyArrayOf } from 'util/array'
import { MetaArray } from 'util/metaPath'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { ProcedimentoModel } from 'view/atendimentos/model-procedimento'
import {
  addProcedimentoAutomatico,
  ProcedimentosAutomaticosAtendimentos,
  shouldAddProcedimentoAutomatico,
} from 'view/atendimentos/utils/util-procedimentoAutomatico'

interface UseProcedimentoAutomaticoOptions {
  nameProcedimentos: MetaArray<ProcedimentoModel>
  nameCodigosProcedimentosAutomaticosExcluidos: MetaArray<string>
}

interface UseProcedimentoAutomaticoResult {
  addIntervencoesProcedimentosSigtap: (codigoProcedimento: ProcedimentosAutomaticosAtendimentos) => void
  removeProcedimentoAutomatico: (procedimento: ProcedimentoModel) => void
}

export function useProcedimentoAutomatico(options: UseProcedimentoAutomaticoOptions): UseProcedimentoAutomaticoResult {
  const { nameProcedimentos, nameCodigosProcedimentosAutomaticosExcluidos } = options

  const {
    data: { procedimentosAutomaticos },
  } = useProcedimentosAutomaticosQuery({
    fetchPolicy: 'cache-first',
  })

  const { removeItem } = useEditableListField({
    name: nameProcedimentos,
  })

  const {
    input: {
      value: codigosProcedimentosAutomaticosExcluidosValue,
      onChange: onChangeCodigosProcedimentosAutomaticosExcluidos,
    },
  } = usePecField({
    name: nameCodigosProcedimentosAutomaticosExcluidos,
  })

  const {
    input: { onChange: onChangeProcedimentos, value: procedimentosValue },
  } = usePecField({ name: nameProcedimentos })

  const codigosProcedimentosAutomaticosExcluidos: ReadonlyArray<string> =
    codigosProcedimentosAutomaticosExcluidosValue ?? emptyArrayOf<string>()
  const procedimentos: ReadonlyArray<ProcedimentoModel> = procedimentosValue ?? emptyArrayOf<ProcedimentoModel>()

  const addIntervencoesProcedimentosSigtap = (codigoProcedimento: ProcedimentosAutomaticosAtendimentos) => {
    if (
      codigoProcedimento &&
      shouldAddProcedimentoAutomatico(codigoProcedimento, codigosProcedimentosAutomaticosExcluidos)
    ) {
      const novaListaProcedimentos = addProcedimentoAutomatico(
        procedimentos,
        procedimentosAutomaticos,
        codigoProcedimento
      )
      onChangeProcedimentos(novaListaProcedimentos)
    }
  }

  const handleRemove = useCallback(
    (procedimento: ProcedimentoModel) => {
      if (procedimento.automatico) {
        onChangeCodigosProcedimentosAutomaticosExcluidos([
          ...codigosProcedimentosAutomaticosExcluidos,
          procedimento.procedimento.codigo,
        ])
      }
      removeItem(procedimento)
    },
    [codigosProcedimentosAutomaticosExcluidos, onChangeCodigosProcedimentosAutomaticosExcluidos, removeItem]
  )

  return { addIntervencoesProcedimentosSigtap, removeProcedimentoAutomatico: handleRemove }
}
